import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BookOpen, Clock, Calendar, RotateCcw, Loader2, CreditCard, FileText } from 'lucide-react';
import api from '../../utils/api';
import PolicyModal from './PolicyModal';
import RefundModal from './RefundModal';
import { formatDays, formatTime, formatFee, formatDate } from './courseHelpers';

export default function EnrolledCourseCard({ course, onRefundSuccess }) {
  const { t } = useTranslation('planner');
  const [refundLoading, setRefundLoading] = useState(false);
  const [_refundError, setRefundError] = useState(null); // Prefixed - refund UI temporarily disabled
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [showPolicyModal, setShowPolicyModal] = useState(false);

  const schedule = course.schedule;
  const scheduleDisplay = schedule
    ? `${formatDays(schedule.days)} ${formatTime(schedule.start_time)} - ${formatTime(schedule.end_time)}`.trim()
    : t('course.scheduleTBD');

  const tuitionFee = formatFee(course.tuition_fee);
  const textbookFee = formatFee(course.textbook_fee);
  const examFee = formatFee(course.exam_fee);

  const totalFee = (parseFloat(course.tuition_fee) || 0) +
                   (parseFloat(course.textbook_fee) || 0) +
                   (parseFloat(course.exam_fee) || 0);

  const isPrivateCourse = course.potential_course_type === 1;

  const handleRefundConfirm = async () => {
    if (!course.student_course_id) {
      setRefundError(t('refund.cannotProcess'));
      setShowRefundModal(false);
      return;
    }

    setRefundLoading(true);
    setRefundError(null);

    try {
      await api.post(`/student-courses/${course.student_course_id}/refund`);
      setShowRefundModal(false);
      if (onRefundSuccess) {
        onRefundSuccess(course.student_course_id);
      }
    } catch (err) {
      console.error('Refund error:', err);
      setRefundError(err.response?.data?.error || t('refund.failed'));
      setShowRefundModal(false);
    } finally {
      setRefundLoading(false);
    }
  };

  // Show refund button only for fully paid courses with Stripe payment (not already refunded)
  // Refund UI temporarily disabled - keeping logic for future use
  const _canRequestRefund = course.is_paid === 2 &&
                            course.stripe_payment_intent_id &&
                            course.refund_status !== 'full';

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 transition-all duration-200">
      <div className="p-3 sm:p-4">
        <div className="flex items-start gap-2 sm:gap-3">
          {/* Course Icon */}
          <div className="shrink-0 w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 flex items-center justify-center">
            <BookOpen className="w-4 h-4 sm:w-5 sm:h-5" strokeWidth={1.5} />
          </div>

          {/* Course Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <h4 className="font-semibold text-gray-900 dark:text-white text-sm sm:text-base break-words">
                {course.course_title}
              </h4>
              {/* View Policy link */}
              <button
                onClick={() => setShowPolicyModal(true)}
                className="shrink-0 text-[11px] sm:text-xs text-[#589DD5] hover:text-[#4080B0] dark:text-[#7AB8E8] dark:hover:text-[#9CCBF0] font-medium transition-all flex items-center gap-1 group"
              >
                <FileText className="w-3 h-3 group-hover:scale-110 transition-transform" />
                <span className="hidden xs:inline group-hover:underline">{t('course.viewPolicy')}</span>
                <span className="xs:hidden group-hover:underline">{t('course.policy')}</span>
              </button>
              {/* Refund Actions - temporarily disabled
              <div className="shrink-0">
                {refundLoading && (
                  <div className="px-2 py-1 text-xs font-medium text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-transparent dark:border dark:border-amber-600 rounded-md flex items-center gap-1">
                    <Loader2 className="w-3 h-3 animate-spin" />
                    <span className="hidden xs:inline">Processing</span>
                  </div>
                )}
                {!refundLoading && canRequestRefund && !refundError && (
                  <button
                    onClick={() => setShowRefundModal(true)}
                    className="px-2 py-1 text-xs font-medium text-gray-600 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 bg-gray-50 dark:bg-transparent dark:border dark:border-gray-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-md transition-colors flex items-center gap-1"
                  >
                    <RotateCcw className="w-3 h-3" />
                    <span className="hidden xs:inline">Request Refund</span>
                    <span className="xs:hidden">Refund</span>
                  </button>
                )}
                {refundError && (
                  <button
                    onClick={() => setRefundError(null)}
                    className="px-2 py-1 text-xs text-red-600 dark:text-red-400 bg-red-50 dark:bg-transparent dark:border dark:border-red-500 rounded-md hover:bg-red-100 dark:hover:bg-red-900/30"
                  >
                    {refundError} ✕
                  </button>
                )}
              </div>
              */}
            </div>

            {/* Status badges */}
            <div className="flex flex-wrap gap-1 sm:gap-1.5 mt-1.5">
              {isPrivateCourse ? (
                <span className="px-1.5 sm:px-2 py-0.5 bg-purple-100 dark:bg-transparent dark:border dark:border-purple-500 text-purple-700 dark:text-purple-400 text-[10px] sm:text-xs font-medium rounded-full">
                  {t('courseType.private')}
                </span>
              ) : (
                <span className="px-1.5 sm:px-2 py-0.5 bg-indigo-100 dark:bg-transparent dark:border dark:border-indigo-500 text-indigo-700 dark:text-indigo-400 text-[10px] sm:text-xs font-medium rounded-full">
                  {t('courseType.group')}
                </span>
              )}
              {course.is_confirmed && course.is_paid > 0 && (
                <span className="px-1.5 sm:px-2 py-0.5 bg-green-100 dark:bg-transparent dark:border dark:border-green-500 text-green-700 dark:text-green-400 text-[10px] sm:text-xs font-medium rounded-full">
                  {t('status.confirmed')}
                </span>
              )}
              {course.is_confirmed && course.is_paid === 0 && (
                <span className="px-1.5 sm:px-2 py-0.5 bg-amber-100 dark:bg-transparent dark:border dark:border-amber-500 text-amber-700 dark:text-amber-400 text-[10px] sm:text-xs font-medium rounded-full">
                  {t('status.processing')}
                </span>
              )}
              {course.is_paid === 1 && (
                <span className="px-1.5 sm:px-2 py-0.5 bg-yellow-100 dark:bg-transparent dark:border dark:border-yellow-500 text-yellow-700 dark:text-yellow-400 text-[10px] sm:text-xs font-medium rounded-full">
                  {t('status.partial')}
                </span>
              )}
              {course.is_paid === 2 && (
                <span className="px-1.5 sm:px-2 py-0.5 bg-blue-100 dark:bg-transparent dark:border dark:border-blue-500 text-blue-700 dark:text-blue-400 text-[10px] sm:text-xs font-medium rounded-full">
                  {t('status.paid')}
                </span>
              )}
            </div>

            {/* Schedule, Start Date & Purchase Date */}
            <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-1 sm:gap-x-4 sm:gap-y-1 mt-2 text-[11px] sm:text-xs text-gray-500 dark:text-gray-400">
              <span className="flex items-center gap-1">
                <Clock className="w-3 h-3 sm:w-3.5 sm:h-3.5 flex-shrink-0" />
                <span className="break-words">{scheduleDisplay}</span>
                {schedule && <span className="text-gray-400 dark:text-gray-500">{t('course.timezone')}</span>}
              </span>
              {course.start_date && (
                <span className="flex items-center gap-1">
                  <Calendar className="w-3 h-3 sm:w-3.5 sm:h-3.5 flex-shrink-0" />
                  {t('course.starts', { date: formatDate(course.start_date) })}
                </span>
              )}
              {course.payment_date && (
                <span className="flex items-center gap-1">
                  <CreditCard className="w-3 h-3 sm:w-3.5 sm:h-3.5 flex-shrink-0" />
                  {t('course.purchased', { date: formatDate(course.payment_date) })}
                </span>
              )}
            </div>

            {/* Fees Summary */}
            {totalFee > 0 && (
              <div className="mt-2 sm:mt-3 pt-2 sm:pt-3 border-t border-gray-100 dark:border-gray-700">
                <div className="flex flex-wrap gap-2 sm:gap-3 text-[11px] sm:text-xs">
                  {tuitionFee && (
                    <span className="text-gray-600 dark:text-gray-400">
                      {t('fees.tuition')} <span className="font-medium text-gray-900 dark:text-white">{tuitionFee}</span>
                    </span>
                  )}
                  {textbookFee && (
                    <span className="text-gray-600 dark:text-gray-400">
                      {t('fees.book')} <span className="font-medium text-gray-900 dark:text-white">{textbookFee}</span>
                    </span>
                  )}
                  {examFee && (
                    <span className="text-gray-600 dark:text-gray-400">
                      {t('fees.exam')} <span className="font-medium text-gray-900 dark:text-white">{examFee}</span>
                    </span>
                  )}
                </div>
                <div className="mt-1 text-xs sm:text-sm font-semibold text-gray-700 dark:text-gray-300">
                  {t('fees.paid')} ${totalFee.toFixed(2)}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Refund Confirmation Modal */}
      <RefundModal
        isOpen={showRefundModal}
        onClose={() => setShowRefundModal(false)}
        onConfirm={handleRefundConfirm}
        course={course}
        refundAmount={totalFee}
        isLoading={refundLoading}
      />

      {/* Policy Modal */}
      <PolicyModal
        isOpen={showPolicyModal}
        onClose={() => setShowPolicyModal(false)}
        policy={course.policy}
        courseName={course.course_title}
        customPolicyText={course.custom_policy_text}
        customPolicyTextKr={course.custom_policy_text_kr}
      />
    </div>
  );
}
